/**
 * @fileoverview Facebook OAuth connect button component.
 * @module features/settings/components/FacebookOAuthConnect
 */

import { type ReactElement, useState, useEffect } from 'react'
import { Plus, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useActiveOrg } from '@/features/orgs/context/ActiveOrgProvider'
import { OrgIdSchema } from '@/features/orgs/schemas/orgSchemas'
import {
  getFacebookOAuthConfig,
  exchangeFacebookAuthCode,
  completeFacebookOAuth,
  type AdAccountInfo,
} from '../api/facebookOAuthService'
import {
  getIntegrationsStatus,
  INTEGRATIONS_STATUS_QUERY_KEY,
} from '../api/integrationStatusService'
import { useAdPlatformSyncStatus } from '../hooks/useAdPlatformSyncStatus'

declare global {
  interface Window {
    FB?: {
      init: (params: {
        appId: string
        cookie?: boolean
        xfbml?: boolean
        version: string
      }) => void
      login: (
        callback: (response: FacebookLoginResponse) => void,
        options: { config_id: string; response_type: string; override_default_response_type: boolean; auth_type?: string }
      ) => void
    }
    fbAsyncInit?: () => void
  }
}

interface FacebookLoginResponse {
  authResponse?: {
    code?: string
    accessToken?: string
    userID?: string
    expiresIn?: number
  }
  status?: string
}

export interface FacebookOAuthConnectProps {
  /** Callback after connection is successful */
  onConnected?: () => Promise<unknown> | unknown
  /** Show success toasts (default: true). Set to false for onboarding flows. */
  showSuccessToasts?: boolean
  /** Custom button label (default: "Connect") */
  buttonLabel?: string
  /** Custom button icon element */
  buttonIcon?: React.ReactNode
}

/**
 * FacebookOAuthConnect
 *
 * Button that initiates Facebook OAuth flow using Facebook Login for Business.
 * Uses Facebook JS SDK for popup-based login flow.
 *
 * @component
 */
export function FacebookOAuthConnect({ onConnected, showSuccessToasts = true, buttonLabel, buttonIcon }: FacebookOAuthConnectProps): ReactElement {
  const { t } = useTranslation('features-integrations')
  const { activeOrgId } = useActiveOrg()
  const queryClient = useQueryClient()
  const { isSyncing, markConnectionComplete, clearSyncingState } = useAdPlatformSyncStatus('FACEBOOK')

  const [sdkLoaded, setSdkLoaded] = useState(false)
  const [isConnecting, setIsConnecting] = useState(false)
  const [showAccountSelector, setShowAccountSelector] = useState(false)
  const [adAccounts, setAdAccounts] = useState<AdAccountInfo[]>([])
  const [selectedAccountId, setSelectedAccountId] = useState<string>('')
  const [pendingCode, setPendingCode] = useState<string | null>(null)
  const [pendingForceBackfill, setPendingForceBackfill] = useState(false)

  // Fetch OAuth config
  const configQuery = useQuery({
    queryKey: ['facebook', 'oauth', 'config', activeOrgId],
    queryFn: () => {
      if (!activeOrgId) throw new Error('No active organization')
      return getFacebookOAuthConfig(OrgIdSchema.parse(activeOrgId))
    },
    enabled: Boolean(activeOrgId),
    staleTime: 60 * 60 * 1000, // 1 hour
  })

  const integrationStatusQuery = useQuery({
    queryKey: activeOrgId
      ? ['org', activeOrgId, ...INTEGRATIONS_STATUS_QUERY_KEY]
      : INTEGRATIONS_STATUS_QUERY_KEY,
    queryFn: () => {
      if (!activeOrgId) throw new Error('No active organization')
      return getIntegrationsStatus(activeOrgId)
    },
    enabled: Boolean(activeOrgId),
    staleTime: 60 * 1000,
  })

  const facebookIntegrationStatus = integrationStatusQuery.data?.find(
    (status) => status.provider === 'FACEBOOK'
  )
  const shouldForceBackfill =
    facebookIntegrationStatus == null || facebookIntegrationStatus.status === 'NOT_CONFIGURED'

  // Load Facebook SDK
  useEffect(() => {
    if (sdkLoaded || !configQuery.data?.appId) return

    // Check if already loaded
    if (window.FB) {
      window.FB.init({
        appId: configQuery.data.appId,
        cookie: true,
        xfbml: true,
        version: 'v21.0',
      })
      const timeoutId = window.setTimeout(() => {
        setSdkLoaded(true)
      }, 0)
      return () => {
        window.clearTimeout(timeoutId)
      }
    }

    // Load SDK script
    window.fbAsyncInit = () => {
      if (window.FB && configQuery.data?.appId) {
        window.FB.init({
          appId: configQuery.data.appId,
          cookie: true,
          xfbml: true,
          version: 'v21.0',
        })
        setSdkLoaded(true)
      }
    }

    const script = document.createElement('script')
    script.id = 'facebook-jssdk'
    script.src = 'https://connect.facebook.net/en_US/sdk.js'
    script.async = true
    script.defer = true

    const firstScript = document.getElementsByTagName('script')[0]
    if (firstScript?.parentNode) {
      firstScript.parentNode.insertBefore(script, firstScript)
    } else {
      document.head.appendChild(script)
    }

    return () => {
      // Cleanup
      const existingScript = document.getElementById('facebook-jssdk')
      if (existingScript) {
        existingScript.remove()
      }
    }
  }, [configQuery.data?.appId, sdkLoaded])

  // Exchange code mutation
  const exchangeMutation = useMutation({
    mutationFn: async ({ code, forceBackfill }: { code: string; forceBackfill: boolean }) => {
      if (!activeOrgId) throw new Error('No active organization')
      return exchangeFacebookAuthCode(OrgIdSchema.parse(activeOrgId), code, forceBackfill)
    },
    onSuccess: async (data, variables) => {
      if (data.selectedAdAccountId) {
        // Single account - already saved
        if (variables.forceBackfill) {
          markConnectionComplete()
        } else {
          clearSyncingState()
        }

        if (showSuccessToasts) {
          toast.success(
            t('cards.integrations.facebook.connected', {
              defaultValue: 'Facebook Ads connected successfully',
            })
          )
          if (variables.forceBackfill) {
            toast.info(
              t('cards.integrations.syncStarted', {
                defaultValue: 'Syncing your ad data... This may take a few minutes.',
              }),
              { duration: 5000 }
            )
          }
        }
        setPendingForceBackfill(false)
        await queryClient.invalidateQueries({
          predicate: (query) =>
            Array.isArray(query.queryKey) &&
            query.queryKey.includes('integrations-status')
        })
        await onConnected?.()
        setIsConnecting(false)
      } else if (data.adAccounts.length > 1) {
        // Multiple accounts - show selector
        setAdAccounts(data.adAccounts)
        setPendingCode(variables.code)
        setPendingForceBackfill(variables.forceBackfill)
        setShowAccountSelector(true)
        setIsConnecting(false)
      } else {
        toast.error(
          t('cards.integrations.facebook.noAccounts', {
            defaultValue: 'No active ad accounts found',
          })
        )
        setIsConnecting(false)
      }
    },
    onError: (error: Error) => {
      toast.error(error.message || t('cards.integrations.facebook.error', {
        defaultValue: 'Failed to connect Facebook Ads',
      }))
      setIsConnecting(false)
    },
  })

  // Complete with selected account mutation
  const completeMutation = useMutation({
    mutationFn: async ({ code, adAccountId, forceBackfill }: { code: string; adAccountId: string; forceBackfill: boolean }) => {
      if (!activeOrgId) throw new Error('No active organization')
      return completeFacebookOAuth(OrgIdSchema.parse(activeOrgId), code, adAccountId, forceBackfill)
    },
    onSuccess: async (_, variables) => {
      if (variables.forceBackfill) {
        markConnectionComplete()
      } else {
        clearSyncingState()
      }

      if (showSuccessToasts) {
        toast.success(
          t('cards.integrations.facebook.connected', {
            defaultValue: 'Facebook Ads connected successfully',
          })
        )
        if (variables.forceBackfill) {
          toast.info(
            t('cards.integrations.syncStarted', {
              defaultValue: 'Syncing your ad data... This may take a few minutes.',
            }),
            { duration: 5000 }
          )
        }
      }
      setShowAccountSelector(false)
      setPendingCode(null)
      setPendingForceBackfill(false)
      setAdAccounts([])
      setSelectedAccountId('')
      await queryClient.invalidateQueries({
          predicate: (query) =>
            Array.isArray(query.queryKey) &&
            query.queryKey.includes('integrations-status')
        })
      await onConnected?.()
    },
    onError: (error: Error) => {
      toast.error(error.message || t('cards.integrations.facebook.error', {
        defaultValue: 'Failed to connect Facebook Ads',
      }))
    },
  })

  const handleConnect = (): void => {
    if (!window.FB || !configQuery.data?.configId) {
      toast.error('Facebook SDK not loaded. Please refresh the page.')
      return
    }

    const forceBackfill = shouldForceBackfill
    setPendingForceBackfill(forceBackfill)
    setIsConnecting(true)

    window.FB.login(
      (response: FacebookLoginResponse) => {
        if (response.authResponse?.code) {
          // Success - exchange code for token
          exchangeMutation.mutate({ code: response.authResponse.code, forceBackfill })
        } else {
          // User cancelled or error
          setIsConnecting(false)
          if (response.status === 'unknown') {
            // User closed the popup without authorizing
            toast.info(
              t('cards.integrations.facebook.cancelled', {
                defaultValue: 'Facebook login cancelled',
              })
            )
          }
        }
      },
      {
        config_id: configQuery.data.configId,
        response_type: 'code',
        override_default_response_type: true,
        auth_type: 'rerequest',
      }
    )
  }

  const handleSelectAccount = (): void => {
    if (!pendingCode || !selectedAccountId) return
    completeMutation.mutate({
      code: pendingCode,
      adAccountId: selectedAccountId,
      forceBackfill: pendingForceBackfill,
    })
  }

  const isLoading = configQuery.isLoading || isConnecting || exchangeMutation.isPending
  const isDisabled = !activeOrgId || !sdkLoaded || isLoading

  // Meta developer app unavailable: point to the CSV import instead of a Connect flow that would fail.
  if (configQuery.data && !configQuery.data.connectEnabled) {
    return (
      <p className="max-w-xs text-xs text-muted-foreground">
        {t('cards.integrations.facebook.connectUnavailable', {
          defaultValue: 'Meta connection is temporarily unavailable — use CSV import.',
        })}
      </p>
    )
  }

  // Show syncing state if currently syncing
  if (isSyncing) {
    return (
      <Button
        type="button"
        variant="outline"
        size="lg"
        disabled
        className="gap-1.5"
      >
        <Loader2 className="size-3.5 animate-spin" />
        {t('cards.integrations.syncing', { defaultValue: 'Syncing...' })}
      </Button>
    )
  }

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="lg"
        onClick={handleConnect}
        disabled={isDisabled}
        className="gap-1.5"
      >
        {isLoading ? (
          <Loader2 className="size-3.5 animate-spin" />
        ) : (
          buttonIcon ?? <Plus className="size-3.5" />
        )}
        {isLoading
          ? t('cards.integrations.facebook.buttons.connecting', { defaultValue: 'Connecting...' })
          : buttonLabel ?? t('cards.integrations.facebook.buttons.connect', { defaultValue: 'Connect' })}
      </Button>

      {/* Ad Account Selector Dialog */}
      <Dialog open={showAccountSelector} onOpenChange={setShowAccountSelector}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {t('cards.integrations.facebook.selectAccount.title', {
                defaultValue: 'Select Ad Account',
              })}
            </DialogTitle>
            <DialogDescription>
              {t('cards.integrations.facebook.selectAccount.description', {
                defaultValue: 'Choose which ad account to connect for syncing ad spend data.',
              })}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 pt-4">
            <Select value={selectedAccountId} onValueChange={setSelectedAccountId}>
              <SelectTrigger>
                <SelectValue
                  placeholder={t('cards.integrations.facebook.selectAccount.placeholder', {
                    defaultValue: 'Select an ad account',
                  })}
                />
              </SelectTrigger>
              <SelectContent>
                {adAccounts.map(account => (
                  <SelectItem key={account.id} value={account.id}>
                    <div className="flex flex-col">
                      <span className="font-medium">{account.name}</span>
                      <span className="text-xs text-muted-foreground">
                        {account.id}
                        {account.currency && ` · ${account.currency}`}
                      </span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowAccountSelector(false)
                  setPendingCode(null)
                  setPendingForceBackfill(false)
                }}
              >
                {t('common.cancel', { defaultValue: 'Cancel' })}
              </Button>
              <Button
                type="button"
                onClick={handleSelectAccount}
                disabled={!selectedAccountId || completeMutation.isPending}
              >
                {completeMutation.isPending ? (
                  <>
                    <Loader2 className="size-4 mr-2 animate-spin" />
                    {t('cards.integrations.facebook.buttons.connecting', {
                      defaultValue: 'Connecting...',
                    })}
                  </>
                ) : (
                  t('cards.integrations.facebook.buttons.connect', { defaultValue: 'Connect' })
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default FacebookOAuthConnect
